import type { StromTermin } from './strom'

/**
 * **Termin ↔ Aufgabe ↔ Mensch** ([Z-24](../../../../../docs/phasen/zusatz-24-my-work.md) AP 24.17,
 * 24.19) – reine Rechnung, ohne React.
 *
 * | | Regel | Quelle |
 * |---|---|---|
 * | fest | festgehalten im/zum Termin oder von Hand dazugelegt | `termin_bezug` dieses Termins |
 * | offen vom letzten Mal | fest an einer früheren Runde derselben Serie und noch offen | `termin_bezug` der Serie |
 * | zwischen euch | ein Teilnehmer bearbeitet sie und ich nehme ab – oder umgekehrt | Bestand im Browser |
 *
 * Teilnehmer werden über ihre Mailadresse zu Work-Personen (`people.all` trägt `email`); wer keine
 * Work-Person ist, steht „extern" am Tisch.
 */

export interface BezugAufgabe {
  id: string
  title: string
  status: string
  assigneeId?: string | null
  assigneeName?: string | null
  workAssigneeId?: string | null
  abnahmeDurchId?: string | null
}

export interface BezugPerson {
  id: string
  displayName: string
  email: string | null
}

export interface FesterBezug {
  terminId: string
  serieId: string | null
  terminBeginn: string
  terminTitel: string
  taskId: string
}

const OFFEN = (s: string) => s !== 'erledigt' && s !== 'zurueckgestellt'
const bearbeiter = (a: BezugAufgabe) => a.workAssigneeId ?? a.assigneeId ?? null

/** Gehört ein gespeicherter Bezug zu diesem Termin? Zuerst die Kennung, sonst Beginn + Titel. */
export function gehoertZu(b: FesterBezug, t: StromTermin): boolean {
  if (b.terminId === t.id) return true
  return b.terminBeginn === t.beginn.slice(0, 19) && b.terminTitel === t.betreff
}

/** Teilnehmer → Work-Person über die Adresse. Ohne Adresse oder ohne Treffer: `null`. */
export function teilnehmerPersonen(
  t: Pick<StromTermin, 'teilnehmer' | 'teilnehmerAdressen'>,
  personen: readonly BezugPerson[],
): { name: string; person: BezugPerson | null }[] {
  const nachAdresse = new Map(
    personen.filter((p) => p.email).map((p) => [p.email!.toLowerCase(), p] as const),
  )
  const namen = t.teilnehmer ?? []
  const adressen = t.teilnehmerAdressen ?? []
  return namen.map((name, i) => ({
    name,
    person: (adressen[i] && nachAdresse.get(adressen[i]!.toLowerCase())) || null,
  }))
}

export interface TerminAufgaben {
  fest: BezugAufgabe[]
  offenLetztesMal: BezugAufgabe[]
  zwischenEuch: { aufgabe: BezugAufgabe; mit: string }[]
}

export function terminAufgaben({
  termin,
  feste,
  serienFeste,
  aufgaben,
  personen,
  ichId,
}: {
  termin: StromTermin
  /** Feste Bezüge des Tages. */
  feste: readonly FesterBezug[]
  /** Feste Bezüge früherer Runden derselben Serie. */
  serienFeste: readonly FesterBezug[]
  aufgaben: ReadonlyMap<string, BezugAufgabe>
  personen: readonly BezugPerson[]
  ichId: string | null
}): TerminAufgaben {
  const festIds = new Set(feste.filter((b) => gehoertZu(b, termin)).map((b) => b.taskId))
  const fest = [...festIds].flatMap((id) => {
    const a = aufgaben.get(id)
    return a ? [a] : []
  })

  const frueher = serienFeste.filter(
    (b) => !gehoertZu(b, termin) && b.terminBeginn < termin.beginn.slice(0, 19),
  )
  const offenLetztesMal = [...new Set(frueher.map((b) => b.taskId))]
    .filter((id) => !festIds.has(id))
    .flatMap((id) => {
      const a = aufgaben.get(id)
      return a && OFFEN(a.status) ? [a] : []
    })

  const gezeigt = new Set([...festIds, ...offenLetztesMal.map((a) => a.id)])
  const andere = teilnehmerPersonen(termin, personen).filter(
    (x): x is { name: string; person: BezugPerson } => !!x.person && x.person.id !== ichId,
  )
  const zwischenEuch: TerminAufgaben['zwischenEuch'] = []
  if (ichId && andere.length) {
    const nachId = new Map(andere.map((x) => [x.person.id, x.name]))
    for (const a of aufgaben.values()) {
      if (gezeigt.has(a.id) || !OFFEN(a.status)) continue
      const b = bearbeiter(a)
      if (b && nachId.has(b) && a.abnahmeDurchId === ichId) {
        zwischenEuch.push({ aufgabe: a, mit: nachId.get(b)! })
      } else if (b === ichId && a.abnahmeDurchId && nachId.has(a.abnahmeDurchId)) {
        zwischenEuch.push({ aufgabe: a, mit: nachId.get(a.abnahmeDurchId)! })
      }
    }
  }
  return { fest, offenLetztesMal, zwischenEuch }
}

export interface AmTischZeile {
  name: string
  person: BezugPerson | null
  inArbeit: BezugAufgabe[]
  /** Wartet bei der Person auf Abnahme durch mich. */
  zurAbnahme: BezugAufgabe[]
}

/** **Am Tisch** – je Teilnehmer, woran er arbeitet und was bei mir zur Abnahme liegt. Ich nicht. */
export function amTisch({
  termin,
  aufgaben,
  personen,
  ichId,
}: {
  termin: StromTermin
  aufgaben: ReadonlyMap<string, BezugAufgabe>
  personen: readonly BezugPerson[]
  ichId: string | null
}): AmTischZeile[] {
  return teilnehmerPersonen(termin, personen)
    .filter((x) => !x.person || x.person.id !== ichId)
    .map(({ name, person }) => {
      if (!person) return { name, person, inArbeit: [], zurAbnahme: [] }
      const seine = [...aufgaben.values()].filter((a) => bearbeiter(a) === person.id)
      return {
        name,
        person,
        inArbeit: seine.filter((a) => a.status === 'in_arbeit'),
        zurAbnahme: seine.filter((a) => a.status === 'review' && a.abnahmeDurchId === ichId),
      }
    })
}
